/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Common Wizard component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.2.0
 */

import { FC, ReactNode, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { TInstructionLink } from '@modules/internationalization/types/corporate';
import { IRootReducer } from '@reducers/rootReducer';

import { Button, LinkBlock, PageTitle } from '../../../components/common';
import BackButton from '../../../components/common/BackButton/BackButton';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { EBasketType } from '../../app/appConstants';
import { makeSelectWizardTranslation } from '../../internationalization/intSelectors';
import {
    IWizardControlsTranslation,
    IWizardPropsTranslation,
    IWizardTranslation,
} from '../../internationalization/types/wizards/wizard';
import { Loader } from '../../loader/components/Loader';
import { getItemFromLocalStorage } from '../../storage/storageUtils';
import {
    wizardBackAction,
    wizardCancelAction,
    wizardInitCurrentSagaAction,
    wizardNextAction,
    wizardRestoreAction,
    wizardSetEntityIdAction,
    wizardSetIsRequestsSticky,
    wizardStartAction,
} from '../wizardActions';
import { EWizardName, WIZARD_ALL_ENTITIES } from '../wizardConstants';
import {
    makeSelectWizardIsFinal,
    makeSelectWizardIsShowLoader,
    makeSelectWizardIsStart,
    makeSelectWizardStepName,
    makeSelectWizardSteps,
} from '../wizardSelectors';
import { IStorageStepsData, IWizardRestorePayload, IWizardStartPayload } from '../wizardTypes';
import {
    WizardBodyStyled,
    WizardControlsGroupStyled,
    WizardControlsItemStyled,
    WizardControlsStyled,
    WizardFooterStyled,
    WizardHeaderItemStyled,
    WizardHeaderStyled,
    WizardStyled,
} from './styled/WizardStyled';

export interface IWizardProps {
    afterStepsComponent?: ReactNode;
    basketType: EBasketType;
    children: ReactNode;
    entityId?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getStartData?: () => any;
    isCancelDisabled?: boolean;
    isBackDisabled?: boolean;
    isNextDisabled?: boolean;
    isRequestsSticky?: boolean;
    onWizardNextHandler?: () => void;
    name: EWizardName;
    skipRestore?: boolean;
    titleElements?: ReactNode;
    translationProps: IWizardPropsTranslation;
    validateError?: string;
    operation?: string;
    instructionLink?: TInstructionLink;
}

export type TWizard = IWizardProps & IConnectedProps & IDispatchProps;

export const Wizard: FC<TWizard> = (props) => {

    const isFastActionsPage = getIsFastActionPage();

    const controlTranslation: IWizardControlsTranslation = useMemo(() => {
        return props.translationProps.controls || props.wizardTranslation.controls;
    }, [props.translationProps.controls, props.wizardTranslation.controls]);

    const wizardNext = () => {
        if (props.isNextDisabled) return;

        if (props.onWizardNextHandler) {
            props.onWizardNextHandler();
        } else {
            props.wizardNext({});
        }
    };

    useEffect(() => {
        props.wizardSetIsRequestsSticky(props.isRequestsSticky ?? false);
        props.wizardInitCurrentSaga(props.name);
        props.wizardSetEntityId(props.entityId ?? WIZARD_ALL_ENTITIES);

        const wizardSteps = getItemFromLocalStorage(props.name);
        if (
            !props.skipRestore &&
            Object.keys(wizardSteps).length &&
            !isFastActionsPage
        ) {
            const storageStepsData: IStorageStepsData = Object.values(wizardSteps)[0] as unknown as IStorageStepsData;
            props.wizardRestore({
                basketType: props.basketType,
                name: props.name,
                basketSnapshotId: Object.keys(wizardSteps)[0],
                basketSnapshotType: storageStepsData.basketSnapshotType,
                storageStepsData: storageStepsData,
                data: props.getStartData?.() ?? undefined,
            });
        } else {
            props.wizardStart({
                basketType: props.basketType,
                name: props.name,
                data: props.getStartData?.() ?? undefined,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <WizardStyled>
        <Loader showLoader={props.isShowLoader}/>
        <PageTitle
            dataName={props.name}
            instructionLink={props.instructionLink}
            title={props.translationProps.title}
        >
            {props.titleElements}
        </PageTitle>
        <WizardHeaderStyled>
            {
                props.steps.map((step, i) => <WizardHeaderItemStyled
                    data-name={`wizard-header-step-${i}`}
                    isActive={step === props.stepName}
                    isPast={props.steps.indexOf(props.stepName) > i}
                    key={step}>
                    <i>{i + 1}</i> {props.translationProps.steps[step]}
                </WizardHeaderItemStyled>)
            }
            {props.afterStepsComponent}
        </WizardHeaderStyled>
        <WizardBodyStyled>
            {props.children}
        </WizardBodyStyled>
        <WizardFooterStyled>
            <WizardControlsStyled>
                <div>
                    {
                        !props.isStart &&
                        <WizardControlsItemStyled>
                            <BackButton disabled={props.isShowLoader}
                                        title={controlTranslation.prev}
                                        onClick={props.wizardBack}/>
                        </WizardControlsItemStyled>
                    }
                </div>
                <WizardControlsGroupStyled>
                    <WizardControlsItemStyled>
                        <LinkBlock
                            data-name="cancelButton"
                            disabled={props.isCancelDisabled || props.isShowLoader}
                            onClick={props.wizardCancel}
                        >
                            {controlTranslation.cancel}
                        </LinkBlock>
                    </WizardControlsItemStyled>
                    <ArrowTooltip
                        disableHoverListener={!props.isNextDisabled && !props.validateError}
                        message={props.validateError}
                        placement={'top-end'}
                    >
                        <WizardControlsItemStyled>
                            <Button
                                dataName="nextButton"
                                disabled={props.isNextDisabled || props.isShowLoader}
                                kind="mediumNormal"
                                type="submit"
                                onClick={wizardNext}>
                                {
                                    props.isFinal
                                        ? controlTranslation.finish
                                        : controlTranslation.next
                                }
                            </Button>
                        </WizardControlsItemStyled>
                    </ArrowTooltip>
                </WizardControlsGroupStyled>
            </WizardControlsStyled>
        </WizardFooterStyled>
    </WizardStyled>;
};

export interface IConnectedProps {
    isFinal: boolean;
    isShowLoader: boolean;
    isStart: boolean;
    stepName: string;
    steps: string[];
    wizardTranslation: IWizardTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isFinal: makeSelectWizardIsFinal(),
    isShowLoader: makeSelectWizardIsShowLoader(),
    isStart: makeSelectWizardIsStart(),
    stepName: makeSelectWizardStepName(),
    steps: makeSelectWizardSteps(),
    wizardTranslation: makeSelectWizardTranslation(),
});

export interface IDispatchProps {
    wizardCancel: () => void;
    wizardInitCurrentSaga: (payload: EWizardName) => void;
    wizardNext: (payload: any) => void;
    wizardBack: () => void;
    wizardRestore: (payload: IWizardRestorePayload) => void;
    wizardSetEntityId: (payload: number) => void;
    wizardSetIsRequestsSticky: (payload: boolean) => void;
    wizardStart: (payload: IWizardStartPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    wizardCancel: () => dispatch(wizardCancelAction()),
    wizardInitCurrentSaga: (payload) => dispatch(wizardInitCurrentSagaAction(payload)),
    wizardNext: (payload) => dispatch(wizardNextAction(payload)),
    wizardBack: () => dispatch(wizardBackAction()),
    wizardRestore: (payload) => dispatch(wizardRestoreAction(payload)),
    wizardSetEntityId: (payload) => dispatch(wizardSetEntityIdAction(payload)),
    wizardSetIsRequestsSticky: (payload => dispatch(wizardSetIsRequestsSticky(payload))),
    wizardStart: (payload) => dispatch(wizardStartAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Wizard);
